import React, { useState, useEffect, useRef, PointerEvent, useCallback, useMemo } from 'react';
import { Canvas, useThree, ThreeEvent } from '@react-three/fiber'
import { Vector3, PerspectiveCamera, Vector2, Euler } from 'three';
import { DomEvent } from '@react-three/fiber/dist/declarations/src/core/events';
import { useDaily, useMeetingState, useAppMessage, useParticipantIds } from '@daily-co/daily-react';
import _ from 'lodash';
import { SetUserPositionAndRotationWorldMessage, Bounds, AppMessage } from '../../../models/Types';
import { DailyEventObjectAppMessage, DailyEventObjectParticipant } from '@daily-co/daily-js';
import { createAppMessage } from '../../../utils/call';
import useCameraState from './UseCameraState';
import { useCameraControls, CameraControlsContextValue } from '../../react/context/CameraControlsCallbackContext';
import { getIsSlowSettingEnabled } from '../../../services/LocalStorage';

export const CameraControls = ({ bounds, containerRef }: any) => {
  const { camera, invalidate } = useThree();
  const { cameraPosition, cameraRotation } = useCameraState(camera);
  const hudCamControls = useCameraControls() as CameraControlsContextValue;

  const [keys, setKeys] = useState({ forwardMouse: false, backwardMouse: false, forwardKey: false, backwardKey: false, left: false, right: false, up: false, down: false, lookLeft: false, lookRight: false, shift: false });

  const moveSpeedRef = useRef(0.125);
  const rotationSpeedRef = useRef(0.03);

  const sendAppMessage = useAppMessage();
  const callObject = useDaily();
  const isSendingAppMessageAfterDelay = useRef(false);

  const cameraMovementAndRotationInterval = useRef<NodeJS.Timer>();
  const localFps = useRef(60);
  const networkFps = useRef(20);

  const [slow] = useState(getIsSlowSettingEnabled());
  const slowFactor = useRef(3);

  useEffect(() => {
    if (!hudCamControls) return;

    const handleMoveForward = (isActive: boolean) => {
      setKeys((prevState) => ({ ...prevState, forwardKey: isActive }));
    };
    const handleMoveBackward = (isActive: boolean) => {
      setKeys((prevState) => ({ ...prevState, backwardKey: isActive }));
    };
    const handleTurnLeft = (isActive: boolean) => {
      setKeys((prevState) => ({ ...prevState, lookLeft: isActive }));
    };
    const handleTurnRight = (isActive: boolean) => {
      setKeys((prevState) => ({ ...prevState, lookRight: isActive }));
    };

    const moveForwardSubscription = hudCamControls.onMoveForward(handleMoveForward);
    const moveBackardSubscription = hudCamControls.onMoveBackward(handleMoveBackward);
    const turnLeftSubscription = hudCamControls.onTurnLeft(handleTurnLeft);
    const turnRightSubscription = hudCamControls.onTurnRight(handleTurnRight);

    return () => {
      moveForwardSubscription();
      moveBackardSubscription();
      turnLeftSubscription();
      turnRightSubscription();
    };
    
  }, [hudCamControls]);

  const sendAppMessageAfterDelay = useCallback(async (maxWaitMs: number = 10000) => {
    let currentWaitMs = 10;
    if (isSendingAppMessageAfterDelay.current) return;
    isSendingAppMessageAfterDelay.current = true;
    while (currentWaitMs < maxWaitMs && !!callObject && callObject.meetingState() !== 'joined-meeting') {
      await new Promise(resolve => setTimeout(resolve, currentWaitMs));
      currentWaitMs *= 2;
    }
    isSendingAppMessageAfterDelay.current = false;
    sendUserPositionRotationMessage();
  }, [callObject]);

  useAppMessage({
    onAppMessage: useCallback((message: DailyEventObjectAppMessage) => {
      const appMessage = createAppMessage(message);
      if (appMessage.message.messageType !== 'world' || appMessage.message.worldMessageType !== 'get-initial-state') return;
      sendUserPositionRotationMessage(message.fromId);
    }, []),
  });

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();

    if (key === 'w' || key === 'arrowup') setKeys((prevState) => ({ ...prevState, forwardKey: true }));
    if (key === 's' || key === 'arrowdown') setKeys((prevState) => ({ ...prevState, backwardKey: true }));
    if (key === 'a') setKeys((prevState) => ({ ...prevState, left: true }));
    if (key === 'd') setKeys((prevState) => ({ ...prevState, right: true }));
    if (key === 'arrowleft') setKeys((prevState) => ({ ...prevState, lookLeft: true }));
    if (key === 'arrowright') setKeys((prevState) => ({ ...prevState, lookRight: true }));
    if (key === 'shift') setKeys((prevState) => ({ ...prevState, shift: true }));
  }, []);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();

    if (key === 'w' || key === 'arrowup') setKeys((prevState) => ({ ...prevState, forwardKey: false }));
    if (key === 's' || key === 'arrowdown') setKeys((prevState) => ({ ...prevState, backwardKey: false }));
    if (key === 'a') setKeys((prevState) => ({ ...prevState, left: false }));
    if (key === 'd') setKeys((prevState) => ({ ...prevState, right: false }));
    if (key === 'arrowleft') setKeys((prevState) => ({ ...prevState, lookLeft: false }));
    if (key === 'arrowright') setKeys((prevState) => ({ ...prevState, lookRight: false }));
    if (key === 'shift') setKeys((prevState) => ({ ...prevState, shift: false }));
  }, []);

  const handleContextMenu = useCallback((event: any) => {
    event.preventDefault();
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [camera, handleKeyDown, handleKeyUp]);

  useEffect(() => {
    const currentContainer = containerRef.current;
    if (currentContainer === null) return;

    currentContainer.addEventListener('contextmenu', handleContextMenu);

    return () => {
      currentContainer.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [containerRef]);


  const getMovementVector = useCallback(() => {
    const xMove = keys.left ? -1 : keys.right ? 1 : 0;
    const yMove = keys.up ? 1 : keys.down ? -1 : 0;
    const zMove = keys.forwardKey || keys.forwardMouse ? -1 : keys.backwardKey || keys.backwardMouse ? 1 : 0;
    return new Vector3(xMove, yMove, zMove);
  }, [keys]);

  const clampCameraToBounds = useCallback(() => {
    const sceneBounds = bounds as Bounds;
    const buffer = 10;
    camera.position.x = _.clamp(camera.position.x, sceneBounds.min.x + buffer, sceneBounds.max.x - buffer);
    camera.position.z = _.clamp(camera.position.z, sceneBounds.min.z + buffer, sceneBounds.max.z - buffer);
  }, [bounds, cameraPosition.x, cameraPosition.z]);

  const sendUserPositionRotationMessage = useCallback((userId = '*') => {
    const rotation = camera.rotation.clone();
    const positionRotationMessage: SetUserPositionAndRotationWorldMessage = {
      messageType: 'world',
      worldMessageType: 'set-user-position-and-rotation',
      position: camera.position.clone(),
      headRotation: new Vector3(rotation.x, rotation.y, rotation.z),
      movement: getMovementVector(),
    }
    if (callObject && callObject.meetingState() === 'joined-meeting') {
      try {
        sendAppMessage(positionRotationMessage, userId);
      } catch (e) {
        console.warn('could not send message', e);
      }
    } else {
      sendAppMessageAfterDelay();
    }
  }, [cameraPosition, cameraRotation, callObject, getMovementVector, sendAppMessage, sendAppMessageAfterDelay]);

  const throttledSendMessage = useCallback(_.throttle(() => {
    sendUserPositionRotationMessage();
  }, 1000 / networkFps.current), []);

  const debouncedSendMessage = useCallback(_.debounce(() => {
    sendUserPositionRotationMessage();
  }, 1000 / networkFps.current), []);

  useEffect(() => {
    clampCameraToBounds();
    throttledSendMessage(); // Ensure first message is sent
    debouncedSendMessage(); // Ensure last message is sent
  }, [cameraPosition, cameraRotation]);

  const isSomeMovementOrRotationKeyPressed = useMemo(() => {
    const { shift, ...keysWithoutShift } = keys;
    return Object.values(keysWithoutShift).some(value => value === true);
  }, [keys]);

  useEffect(() => {
    const userMovementOrRotation = isSomeMovementOrRotationKeyPressed;
    if (userMovementOrRotation) {
      if (cameraMovementAndRotationInterval.current) clearInterval(cameraMovementAndRotationInterval.current);
      cameraMovementAndRotationInterval.current = setInterval(updateCameraPositionAndRotation, (1000 / localFps.current) * (slow ? slowFactor.current : 1));
    } else if (cameraMovementAndRotationInterval.current){
      clearInterval(cameraMovementAndRotationInterval.current);
      cameraMovementAndRotationInterval.current = undefined;
    }
  }, [isSomeMovementOrRotationKeyPressed, keys]);

  const updateCameraPositionAndRotation = useCallback(() => {
    if (!camera) return;
    invalidate();
    const moveSpeed = moveSpeedRef.current * (keys.shift ? 2 : 1) * (slow ? slowFactor.current : 1);
    const rotationSpeed = rotationSpeedRef.current * (keys.shift ? 1.5 : 1) * (slow ? slowFactor.current : 1);

    if (keys.forwardKey) camera.position.add(camera.getWorldDirection(new Vector3()).multiplyScalar(moveSpeed));
    if (keys.backwardKey) camera.position.sub(camera.getWorldDirection(new Vector3()).multiplyScalar(moveSpeed));
    if (keys.left) camera.position.sub(camera.getWorldDirection(new Vector3()).cross(camera.up).multiplyScalar(moveSpeed * 1.75));
    if (keys.right) camera.position.add(camera.getWorldDirection(new Vector3()).cross(camera.up).multiplyScalar(moveSpeed * 1.75));
    if (keys.lookLeft) camera.rotateOnAxis(new Vector3(0, 1, 0), rotationSpeed);
    if (keys.lookRight) camera.rotateOnAxis(new Vector3(0, 1, 0), -rotationSpeed);
  }, [camera, keys, slow, slowFactor]);

  return null;
};
